import { Children, cloneElement, createElement, isValidElement } from "react";
import { ClipPath, Defs, G, Path, Rect, Svg, Text, Tspan, View } from "@react-pdf/renderer";

import { DEFAULT_ICON_SIZE, IconProps, resolveIconToReactSvg } from "lib/icons/untitled_ui/UIcon";
import { applyColorOverrides, resolveColor } from "./utils";

const SVG_ELEMENT_MAP: Partial<Record<string, React.ElementType>> = {
  svg: Svg,
  rect: Rect,
  path: Path,
  g: G,
  defs: Defs,
  clippath: ClipPath,
  text: Text,
  tspan: Tspan,
};

type ColorContext = { color: string; fill: string };

function convertNode(
  node: React.ReactNode,
  index?: number,
  colors?: ColorContext,
  inheritedFill?: string,
): React.ReactNode {
  if (node === null || node === undefined) return null;
  if (typeof node === "string" || typeof node === "number") return node;
  if (!isValidElement(node)) return null;

  const { type, props } = node;

  if (typeof type !== "string") return null;

  const PdfComponent = SVG_ELEMENT_MAP[type.toLowerCase()];
  if (!PdfComponent) {
    if (!["title"].includes(type.toLowerCase())) {
      // Warn about any unsupported SVG elements which aren't expected to not render (like <title>)
      console.warn(`Unsupported SVG element in PDF export: ${type}`);
    }
    return null;
  }

  const allProps = props as Record<string, unknown>;
  const svgProps = Object.fromEntries(
    Object.entries(allProps).filter(([k]) => k !== "children" && k !== "xmlns"),
  );
  const rawChildren = allProps["children"];
  const children =
    isValidElement(rawChildren) || Array.isArray(rawChildren) ? rawChildren : undefined;

  // We need to manually handle the colours ourselves since it doesn't seem like inheritance exists
  if (colors) {
    Object.assign(svgProps, applyColorOverrides(svgProps, colors.color, inheritedFill));
  }

  const nextInheritedFill =
    "fill" in svgProps && typeof svgProps.fill === "string" ? svgProps.fill : inheritedFill;

  const convertedChildren = Children.toArray(children).flatMap((child, i) => {
    const converted = convertNode(child, i, colors, nextInheritedFill);
    return converted !== null ? [converted] : [];
  });

  return createElement(PdfComponent, { ...svgProps, key: index }, ...convertedChildren);
}

const convertReactSvgToPdfSvg = (svg: JSX.Element, colors?: ColorContext): JSX.Element => {
  const result = convertNode(svg, undefined, colors);
  if (!isValidElement(result)) {
    throw new Error("Failed to convert SVG element to react-pdf SVG");
  }
  return result;
};

export const PdfUIcon = ({ icon, size, fill, color, background }: IconProps) => {
  const svg = resolveIconToReactSvg(icon);
  if (!svg || !isValidElement(svg)) {
    throw new Error(`Icon ${icon} not found or isn't valid when rendering for pdf export`);
  }
  const colors: ColorContext = {
    color: resolveColor(color, "sysPrimaryDefault"),
    fill: resolveColor(fill, "sysBackgroundDefault"),
  };

  const iconElement = cloneElement(convertReactSvgToPdfSvg(svg, colors), {
    width: `${size ?? DEFAULT_ICON_SIZE}px`,
    height: `${size ?? DEFAULT_ICON_SIZE}px`,
  });

  if (background) {
    return (
      <View
        style={{
          backgroundColor: resolveColor(background.color, "sysBackgroundDefault"),
          padding: background.padding ?? "4px",
          borderRadius: background.borderRadius ?? "4px",
        }}
      >
        {iconElement}
      </View>
    );
  }

  return iconElement;
};
